import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDropside } from '@components/icons/dropside';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { PlanData, type Question, type QuestionAnswer } from 'api/ai-builder/requests';

import { AiBuilderPlan } from '../../ai-builder-plan';
import { AiBuilderQuestions } from '../../ai-builder-questions';
import type { ToolStatus } from '../../hooks';

const MESSAGE_GAP = pxToRem(12);
const TOOL_RAIL_COLUMN_WIDTH = pxToRem(20);
const railColor = theme.color.border10;

type ToolMessageProps = {
  toolName?: string;
  toolLabel?: string;
  toolDescription?: string;
  status: ToolStatus;
  questions?: Question[];
  plan?: PlanData;
  errorMessage?: string;
  isNextTool: boolean;
  isDescriptionOpen: boolean;
  onToggleDescription: () => void;
  onSubmitAnswers: (answers: QuestionAnswer[]) => Promise<void>;
  onAcceptPlan: () => Promise<void>;
  onRejectPlan: () => Promise<void>;
  isPlanLoading?: boolean;
};

export const ToolMessage = ({
  toolName,
  toolLabel,
  toolDescription,
  status,
  questions,
  plan,
  errorMessage,
  isNextTool,
  isDescriptionOpen,
  onToggleDescription,
  onSubmitAnswers,
  onAcceptPlan,
  onRejectPlan,
  isPlanLoading,
}: ToolMessageProps) => {
  const collapsedContent = errorMessage || toolDescription;

  // Handle ask_user_questions tool with in_progress status
  if (toolName === 'ask_user_questions' && status === 'in_progress' && questions) {
    return (
      <AiBuilderQuestions
        questions={questions}
        onSubmit={(answers) => {
          return onSubmitAnswers(answers);
        }}
      />
    );
  }

  if (toolName === 'suggest_plan' && plan) {
    let planStatus: 'pending' | 'accepted' | 'rejected' = 'pending';
    if (status === 'accepted' || status === 'done') {
      planStatus = 'accepted';
    } else if (status === 'rejected' || status === 'error') {
      planStatus = 'rejected';
    }
    return <AiBuilderPlan plan={plan} status={planStatus} onAccept={onAcceptPlan} />;
  }

  const showDescription = isDescriptionOpen && Boolean(collapsedContent);
  return (
    <ToolGroup $extendGap={isNextTool}>
      <ToolRow>
        <ToolRailColumn>
          <ToolIconWrapper>
            {status === 'done' ? (
              <IconCheckmark color="elementGreen100" />
            ) : status === 'error' ? (
              <IconCloseRegular color="elementRed100" />
            ) : (
              <SizedRelativeSpinner size={16} $skipMargin />
            )}
          </ToolIconWrapper>
          {isNextTool && <ToolRailLine />}
        </ToolRailColumn>
        <ToolContent>
          <ToolHeader>
            <Text font="semibold12" text={toolLabel} />
            {collapsedContent && (
              <IconDropside
                color="buttonIconPositive"
                cursor="pointer"
                rotateDegrees={isDescriptionOpen ? 90 : 0}
                onClick={(e) => {
                  e.stopPropagation?.();
                  onToggleDescription();
                }}
              />
            )}
          </ToolHeader>
        </ToolContent>
      </ToolRow>

      <Collapse isCollapsed={!showDescription} overflowType="visible">
        <ToolRow>
          <ToolRailColumn>{isNextTool && <ToolRailLine />}</ToolRailColumn>
          <ToolContent>
            <div>
              <Text
                font="regular12"
                text={collapsedContent}
                color={errorMessage ? 'elementRed100' : 'textPrimary'}
              />
            </div>
          </ToolContent>
        </ToolRow>
      </Collapse>
    </ToolGroup>
  );
};

const ToolGroup = styled.div<{ $extendGap?: boolean }>(({ $extendGap }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  '&::after': $extendGap
    ? {
        content: '""',
        position: 'absolute',
        top: '100%',
        left: `calc(${TOOL_RAIL_COLUMN_WIDTH} / 2)`,
        transform: 'translateX(-0.5px)',
        width: pxToRem(1),
        height: MESSAGE_GAP,
        backgroundColor: railColor,
      }
    : undefined,
}));

const ToolRow = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'stretch',
  space: 2,
})({});

const ToolRailColumn = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: TOOL_RAIL_COLUMN_WIDTH,
  flexShrink: 0,
  position: 'relative',
});

const ToolRailLine = styled.div({
  width: pxToRem(1),
  flex: '1 1 auto',
  backgroundColor: railColor,
  minHeight: pxToRem(8),
});

const ToolIconWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: pxToRem(20),
  height: pxToRem(20),
  flexShrink: 0,
  zIndex: 1,
  backgroundColor: theme.color.backgroundLightDark,
});

const ToolContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minWidth: 0,
});

const ToolHeader = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 2,
})({
  minHeight: pxToRem(20),
});
